import type { To } from 'react-router';
import React, { useCallback, useId } from 'react';
import { useNavigate } from 'react-router';

import type { IconName } from '../../assets/Icon/Icon';
import type {
  PolymorphicComponentProps,
  PolymorphicRef,
} from '../../utilities/types/polymorphicAsProp';
import { Divider } from '../../assets/Divider/Divider';
import { Icon } from '../../assets/Icon/Icon';
import { selectors, transitions } from '../../controls/shared/styles';
import FeatureBadge from '../../formatting/FeatureBadge/FeatureBadge';
import { colors, darkThemeSelector, fontWeights, shadows, styled } from '../../stitches.config';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { isDefined } from '../../utilities/isDefined';
import { HStack } from '../../utilities/Stack/HStack';
import {
  AccessLevel,
  isAccessLevelVisible,
  useAccessLevelContext,
} from '../../utilities/useAccessLevelContext';
import { useViewport } from '../../utilities/useViewport';
import { Badge } from '../Badge/Badge';
import {
  DropdownMenu,
  DropdownMenuButton,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPopover,
} from '../DropdownMenu/DropdownMenu';
import { IconTooltip } from '../Tooltip/Tooltip';

const TabIcon = styled(Icon, {
  color: '$$iconColor',

  variants: {
    hasSize: {
      'x-small': {
        width: '$12',
        height: '$12',
      },

      small: {
        width: '$16',
        height: '$16',
      },
    },
  },
});

const TabContainer = styled('button', {
  position: 'relative',
  display: 'flex',
  boxShadow: '$$focusRing',
  fontWeight: fontWeights.bold,
  whiteSpace: 'nowrap',
  transition: transitions.control,

  [selectors.focus]: {
    outline: 'none',
    $$focusRing: shadows.focusRingLight,

    [darkThemeSelector]: {
      $$focusRing: shadows.focusRingDark,
    },
  },

  variants: {
    hasAnnotation: {
      true: {
        '@notDesktop': {
          paddingRight: '$6',
        },

        '@desktop': {
          paddingRight: '$4',
        },
      },
    },
    hasSubtabs: {
      true: {},
      false: {},
    },
    internal: {
      true: {},
      false: {},
    },
    disabled: {
      true: {
        cursor: 'not-allowed',
        opacity: 0.5,
      },
      false: {},
    },
    isSelected: {
      true: {
        color: colors.headingBrandLight,
        $$iconColor: colors.bodyBrandLight,
        $$indicatorColor: colors.iconBrandLight,

        [darkThemeSelector]: {
          color: colors.headingBrandDark,
          $$iconColor: colors.bodyBrandDark,
          $$indicatorColor: colors.iconBrandDark,
        },
      },
      false: {
        color: colors.headingNeutralLight,
        $$iconColor: colors.bodyNeutralLight,

        [darkThemeSelector]: {
          color: colors.headingNeutralDark,
          $$iconColor: colors.bodyNeutralDark,
        },

        [selectors.hover]: {
          color: colors.headingNeutralLight,
          $$iconColor: colors.bodyNeutralLight,

          [darkThemeSelector]: {
            color: colors.headingNeutralDark,
            $$iconColor: colors.bodyNeutralDark,
          },
        },
      },
    },
    hasSize: {
      'x-small': {
        '&::after': {
          height: '$2',
        },

        '@notDesktop': {
          padding: '$2 $6',
          borderRadius: '$8',

          '&::after': {
            bottom: '-$6',
          },
        },

        '@desktop': {
          padding: '0 $4',
          borderRadius: '$6',

          '&::after': {
            bottom: '-$8',
          },
        },
      },
      small: {
        '&::after': {
          height: '$4',
        },

        '@notDesktop': {
          padding: '$4 $8',
          borderRadius: '$10',

          '&::after': {
            bottom: '-$10',
          },
        },

        '@desktop': {
          padding: '$4 $6',
          borderRadius: '$10',

          '&::after': {
            bottom: '-$8',
          },
        },
      },
    },
  },
  compoundVariants: [
    {
      hasSubtabs: false,
      isSelected: true,
      css: {
        '&::after': {
          content: '',
          position: 'absolute',
          left: 0,
          right: 0,
          display: 'block',
          backgroundColor: '$$indicatorColor',
          borderRadiusTop: '99em',
        },
      },
    },
    {
      internal: true,
      css: {
        color: colors.internalBodyLight,
        $$iconColor: colors.internalIconLight,
        $$indicatorColor: colors.internalIconLight,

        [selectors.hover]: {
          color: colors.internalHeadingLight,
          $$iconColor: colors.internalBodyLight,
        },

        [darkThemeSelector]: {
          color: colors.internalBodyDark,
          $$iconColor: colors.internalIconDark,
          $$indicatorColor: colors.internalIconDark,

          [selectors.hover]: {
            color: colors.internalHeadingDark,
            $$iconColor: colors.internalBodyDark,
          },
        },
      },
    },
  ],
});

type TabPropSize = 'x-small' | 'small';
export interface TabProps {
  /**
   * The level required to access the tab.
   */
  accessLevel?: AccessLevel | null;
  /**
   * Additional annotation such a result count within the tab.
   * */
  annotation?: React.ReactNode;
  /**
   * Written label for the tab.
   * */
  children?: React.ReactNode;
  /**
   * Set which icon to display, no value displays no icon.
   */
  icon?: IconName;
  /**
   * Boolean to show internal-only styles.
   */
  internal?: boolean;
  /**
   * Key for the tab.
   */
  key?: string;
  /**
   * Written label for the tab.
   */
  label?: React.ReactNode;
  /**
   * Handles the onClick event.
   */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /**
   * Marks the currently selected tab.
   */
  selected?: boolean;
  /**
   * Whether the tab is disabled.
   */
  disabled?: boolean;
  /**
   * Size of the tab.
   */
  size?: TabPropSize;
  /**
   * Whether the tab has subtabs.
   */
  subtabs?: TabProps[];
  /**
   * A tooltip to be displayed on hover.
   */
  tooltip?: React.ReactNode;
  /**
   * Routing
   */
  as?: React.ElementType;
  to?: To;
}

function filterOutNullAccessLevels(tab: TabProps): tab is typeof tab & {
  accessLevel: NonNullable<(typeof tab)['accessLevel']> | undefined;
} {
  return tab.accessLevel !== null;
}

function TabInner<Tag extends React.ElementType>(
  {
    accessLevel: accessLevelProp,
    annotation,
    children,
    icon,
    internal,
    label,
    selected = false,
    disabled = false,
    size = 'small',
    subtabs,
    tooltip,
    ...remaining
  }: PolymorphicComponentProps<Tag, TabProps>,
  forwardedRef: PolymorphicRef<Tag>,
) {
  const accessLevel = useAccessLevelContext(accessLevelProp);
  const isInternal = internal || accessLevel === AccessLevel.Internal;
  if (accessLevel === null) return null;

  return (
    <TabContainer
      type="button"
      role="tab"
      aria-selected={selected ? 'true' : 'false'}
      ref={forwardedRef}
      isSelected={selected}
      hasAnnotation={isDefined(annotation)}
      hasSubtabs={isDefined(subtabs)}
      internal={isInternal}
      hasSize={size}
      disabled={disabled}
      {...remaining}
    >
      <AlignStack
        direction="row"
        gap={size === 'x-small' ? 4 : 6}
        preset={size === 'x-small' ? 'small' : 'body'}
        start={isDefined(icon) && <TabIcon hasSize={size} icon={icon} />}
        end={
          (isDefined(annotation) || accessLevel || tooltip) && (
            <HStack align="center" spacing={4}>
              {isDefined(annotation) && (
                <Badge internal={isInternal} size={size} variant={selected ? 'brand' : 'neutral'}>
                  {annotation}
                </Badge>
              )}
              {accessLevel && isAccessLevelVisible(accessLevel) && (
                <FeatureBadge type={accessLevel} size="x-small" />
              )}
              {tooltip && (
                <IconTooltip
                  icon="question"
                  size={14}
                  contents={tooltip}
                  variant={selected ? 'brand' : 'neutral'}
                />
              )}
            </HStack>
          )
        }
      >
        <Text>{label || children}</Text>
      </AlignStack>
    </TabContainer>
  );
}

export const Tab = React.forwardRef(TabInner) as <D extends React.ElementType = 'button'>(
  props: PolymorphicComponentProps<D, TabProps> & { ref?: PolymorphicRef<D> },
) => ReturnType<typeof TabInner>;

const TabDivider = styled(Divider, {
  padding: '$4',
});

const TabsContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '$4',
  alignItems: 'center',
  height: '100%',

  '@notDesktop': {
    width: '100%',
  },
});

export type TabsProps = {
  /**
   * Pass `Tab` components into this for automatically laid out tabs.
   * */
  children?: React.ReactNode;
  /**
   * Size of the tabs.
   */
  size?: TabPropSize;
  /**
   * Tabs passed in as an array.
   */
  tabs?: TabProps[];
  /**
   * Secondary tabs passed in as an array.
   */
  secondaryTabs?: TabProps[];
};

function TabsArray({ size, tabs, secondaryTabs }: TabsProps) {
  const { breakpoint } = useViewport();
  const navigate = useNavigate();
  const key = useId();
  const selectedTab =
    tabs?.find((tab) => tab.selected) || secondaryTabs?.find((tab) => tab.selected);
  const selectedSubtab = selectedTab?.subtabs?.find((subtab) => subtab.selected);

  const onClickTab = useCallback(
    (tab: TabProps, e: Event) => {
      if (tab.to) {
        navigate(tab.to);
      }

      tab.onClick?.(e as unknown as React.MouseEvent<HTMLButtonElement>);
    },
    [navigate],
  );

  const filteredTabs = tabs?.filter(filterOutNullAccessLevels);
  const filteredSecondaryTabs = secondaryTabs?.filter(filterOutNullAccessLevels);

  return breakpoint === 'desktop' ? (
    <>
      {filteredTabs?.map((tab) => (
        <React.Fragment key={`${key}-tab-${tab.label}`}>
          <Tab size={size} {...tab} />
          {tab.selected && tab.subtabs?.length && (
            <>
              <Icon icon="chevron-right" size={12} variant="brand" />
              {tab.subtabs?.map((subtab) => (
                <Tab size={size} key={`${key}-subtab-${subtab.label}`} {...subtab} />
              ))}
            </>
          )}
        </React.Fragment>
      ))}
      {!!filteredTabs?.length && !!filteredSecondaryTabs?.length && (
        <TabDivider appearance="thick" direction="column" variant="neutral" />
      )}
      {filteredSecondaryTabs?.map((secondaryTab) => (
        <React.Fragment key={`${key}-secondaryTab-${secondaryTab.label}`}>
          <Tab size={size} {...secondaryTab} />
          {secondaryTab.selected && secondaryTab.subtabs?.length && (
            <>
              <Icon icon="chevron-right" size={12} variant="brand" />
              {secondaryTab.subtabs?.map((subtab) => (
                <Tab size={size} key={`${key}-subtab-${subtab.label}`} {...subtab} />
              ))}
            </>
          )}
        </React.Fragment>
      ))}
    </>
  ) : (
    <DropdownMenu>
      <DropdownMenuButton
        arrangement="leading-icon"
        icon={selectedTab?.icon}
        labelAlign="start"
        labelStretch
        size="medium"
        width="100%"
        annotation={selectedTab?.annotation}
      >
        {selectedTab?.label} {selectedSubtab ? ` > ${selectedSubtab.label}` : null}
      </DropdownMenuButton>
      <DropdownMenuPopover align="start" matchTrigger>
        {!!filteredTabs?.length && (
          <DropdownMenuGroup>
            {filteredTabs.map((tab) => {
              const hasSubtabs = tab.subtabs?.length;
              if (hasSubtabs) {
                return (
                  <DropdownMenuGroup key={`${key}-tab-${tab.label}`} label={tab.label} isNested>
                    {tab.subtabs?.map((subtab) => (
                      <DropdownMenuItem
                        key={`${key}-subtab-${subtab.label}`}
                        onSelect={(e) => onClickTab(subtab, e)}
                        accessLevel={subtab.accessLevel ?? undefined}
                        annotation={subtab.annotation}
                        icon={subtab.icon}
                        label={subtab.label || subtab.children}
                        internal={tab.internal}
                        tooltip={tab.tooltip}
                        selected={selectedSubtab?.label === subtab.label}
                        disabled={subtab.disabled}
                      />
                    ))}
                  </DropdownMenuGroup>
                );
              }

              return (
                <DropdownMenuItem
                  key={`${key}-tab-${tab.label}`}
                  onSelect={(e) => onClickTab(tab, e)}
                  accessLevel={tab.accessLevel}
                  annotation={tab.annotation}
                  icon={tab.icon}
                  label={tab.label || tab.children}
                  internal={tab.internal}
                  tooltip={tab.tooltip}
                  selected={selectedTab?.label === tab.label}
                  disabled={tab.disabled}
                />
              );
            })}
          </DropdownMenuGroup>
        )}
        {!!filteredSecondaryTabs?.length && (
          <DropdownMenuGroup>
            {filteredSecondaryTabs.map((secondaryTab) => (
              <DropdownMenuItem
                key={`${key}-secondaryTab-${secondaryTab.label}`}
                onSelect={(e) => onClickTab(secondaryTab, e)}
                accessLevel={secondaryTab.accessLevel}
                annotation={secondaryTab.annotation}
                icon={secondaryTab.icon}
                label={secondaryTab.label || secondaryTab.children}
                internal={secondaryTab.internal}
                tooltip={secondaryTab.tooltip}
                selected={selectedTab?.label === secondaryTab.label}
                disabled={secondaryTab.disabled}
              />
            ))}
          </DropdownMenuGroup>
        )}
      </DropdownMenuPopover>
    </DropdownMenu>
  );
}

export function Tabs({ children, size, tabs, secondaryTabs, ...remaining }: TabsProps) {
  const isArray = Array.isArray(tabs) || Array.isArray(secondaryTabs);
  return (
    <TabsContainer role="tablist" {...remaining}>
      {isArray ? (
        <TabsArray size={size} tabs={tabs} secondaryTabs={secondaryTabs} />
      ) : (
        children || tabs || secondaryTabs
      )}
    </TabsContainer>
  );
}
